import React, { useState, useEffect } from 'react';
import { Copy, ExternalLink } from 'lucide-react'; 
import { useLocation, useNavigate } from 'react-router-dom';

const VideoGenerationBatch = () => {
    const { search } = useLocation();
    const navigate = useNavigate();

    // --- STATE MANAGEMENT ---
    const [viewMode, setViewMode] = useState('tabs');
    const [activeTab, setActiveTab] = useState('template');
    const [copiedSheetUrl, setCopiedSheetUrl] = useState('');
    const [error, setError] = useState('');

    // --- .ENV CONFIGURATION ---
    const basicTemplateUrl = process.env.REACT_APP_GOOGLE_SHEET_TEMPLATE_BASIC_URL;
    const advancedTemplateUrl = process.env.REACT_APP_GOOGLE_SHEET_TEMPLATE_ADVANCED_URL;

    // --- EFFECT HOOK for OAuth Redirect Handling ---
    useEffect(() => {
        const params = new URLSearchParams(search);
        const newSheetUrl = params.get('newSheetUrl');
        const authError = params.get('error');

        if (newSheetUrl) {
            setViewMode('copy');
            setCopiedSheetUrl(decodeURIComponent(newSheetUrl));
            navigate(window.location.pathname, { replace: true });
        } else if (authError) {
            setError('Failed to create a copy. Please ensure you grant permission and try again.');
            console.error('Error from backend:', authError);
            navigate(window.location.pathname, { replace: true });
        }
    }, [search, navigate]);

    // --- HANDLERS ---
    const handleMakeCopy = (templateType) => {
        setError('');
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        window.location.href = `${apiBaseUrl}/api/auth/google?templateType=${templateType}`;
    };

    // --- UTILITY for URL formatting ---
    const getEmbeddedUrl = (url, isPreview = false) => {
        if (!url || !url.includes('docs.google.com')) return '';
        if (isPreview) {
            return url.replace('/edit', '/preview?rm=minimal&chrome=false');
        }
        // return url.replace('/edit', '/edit?rm=minimal&amp;chrome=false');
        return url;
    };

    // --- RENDER LOGIC ---
    // const renderTabs = () => (
    //     <div className="mb-4 border-b border-gray-200">
    //         <nav className="-mb-px flex space-x-8" aria-label="Tabs">
    //             <button
    //                 onClick={() => setActiveTab('template')}
    //                 className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
    //                     activeTab === 'template'
    //                     ? 'border-blue-600 text-blue-600'
    //                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    //                 }`}
    //             >
    //                 Template-based Version
    //                 <a
    //                     href={basicTemplateUrl}
    //                     target="_blank"
    //                     rel="noopener noreferrer"
    //                     onClick={(e) => e.stopPropagation()}
    //                     className="ml-1.5 inline-flex items-center text-blue-600 hover:underline text-xs font-semibold"
    //                 >
    //                     (go/veomate-sheet <ExternalLink className="w-3 h-3 ml-0.5" />)
    //                 </a>
    //             </button>
    //             <button
    //                 onClick={() => setActiveTab('labeling')}
    //                 className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
    //                     activeTab === 'labeling'
    //                     ? 'border-blue-600 text-blue-600'
    //                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    //                 }`}
    //             >
    //                 Labeling-based Version
    //                 <a
    //                     href={advancedTemplateUrl}
    //                     target="_blank"
    //                     rel="noopener noreferrer"
    //                     onClick={(e) => e.stopPropagation()}
    //                     className="ml-1.5 inline-flex items-center text-blue-600 hover:underline text-xs font-semibold"
    //                 >
    //                     (go/veomate-sheet-advanced <ExternalLink className="w-3 h-3 ml-0.5" />)
    //                 </a>
    //             </button>
    //         </nav>
    //     </div>
    // );
    const renderTabs = () => (
        <div className="relative mb-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                    onClick={() => setActiveTab('template')}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'template'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    Template-based Version
                    <a
                        href={basicTemplateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="ml-1.5 inline-flex items-center text-blue-600 hover:underline text-xs font-semibold"
                    >
                        (go/veomate-sheet <ExternalLink className="w-3 h-3 ml-0.5" />)
                    </a>
                </button>
                <div className="relative group">
                    <button
                        onClick={() => setActiveTab('labeling')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'labeling'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Labeling-based Version
                        <a
                            href={advancedTemplateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="ml-1.5 inline-flex items-center text-blue-600 hover:underline text-xs font-semibold"
                        >
                            (go/veomate-sheet-advanced <ExternalLink className="w-3 h-3 ml-0.5" />)
                        </a>
                    </button>
                    <div className="absolute -top-1 -right-2 transform translate-x-1/2 -translate-y-1/2">
                        {/* “New” Logo */}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-pink-500 to-orange-400 text-white shadow-md">
                            New
                        </span>
                        <div
                            className="absolute bottom-full mb-2 w-72 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                            style={{ left: '50%', transform: 'translateX(-50%)' }} // Center the tooltip
                        >
                            Newly support Image Gen & Editing based on Nano Banana, and Ingredients to Video.
                            <div className="absolute top-full left-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-800" style={{ transform: 'translateX(-50%)' }}></div>
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    );

    const renderTemplateView = (title, templateUrl, templateType) => (
        <div className="flex-grow flex flex-col">
             <div className="flex-grow w-full border border-gray-300 rounded-lg mb-4">
                <iframe
                    src={getEmbeddedUrl(templateUrl, true)}
                    title={title}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                ></iframe>
            </div>
            <button
                onClick={() => handleMakeCopy(templateType)}
                className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center justify-center self-end hover:bg-green-700 transition-colors"
            >
                <Copy className="w-4 h-4 mr-2" />
                Make a Copy and Go
            </button>
        </div>
    );

    return (
        <div className="p-8 bg-white h-full flex flex-col">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">VeoMate Studio (Batch Mode)</h1>
            
            {error && <p className="text-red-500 my-4 p-3 bg-red-50 rounded-md">{error}</p>}

            {viewMode === 'copy' ? (
                <>
                    {/* --- MODIFICATION START --- */}
                    <div className="text-center my-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-lg font-semibold text-gray-800">
                            You Can Edit Your Copy Here or&nbsp;
                            <a
                                href={copiedSheetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-bold text-blue-600 hover:underline inline-flex items-center"
                            >
                                Go to Your Copied Sheet
                                <ExternalLink className="w-4 h-4 ml-1.5" />
                            </a>
                        </p>
                    </div>
                    {/* --- MODIFICATION END --- */}

                    <div className="flex-grow w-full border border-gray-300 rounded-lg">
                        <iframe
                            src={getEmbeddedUrl(copiedSheetUrl, false)}
                            title="Your Copied Google Sheet"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                        ></iframe>
                    </div>
                </>
            ) : (
                <>
                    {renderTabs()}
                    <div className="flex-grow flex flex-col">
                        {activeTab === 'template' && renderTemplateView('Basic Version', basicTemplateUrl, 'basic')}
                        {activeTab === 'labeling' && renderTemplateView('Advanced Version', advancedTemplateUrl, 'advanced')}
                    </div>
                </>
            )}
        </div>
    );
};

export default VideoGenerationBatch;